import { blandAltman, icc11, icc21, type BlandAltman, type ResultadoIcc } from './icc.js';

/**
 * Repetibilidade das medidas: a pergunta da Fase 0 ("quais medidas sao
 * confiaveis o bastante para entrar no protocolo?") e o acompanhamento da
 * rotina (a dupla medicao dos 10% sorteados).
 */

/** Uma observacao no formato longo (como v_measurement_long). */
export interface ObservacaoDeSessao {
  ficha: string;
  trait: string;
  lado: 'E' | 'D' | 'NA';
  avaliador: string;
  rodada: number;
  valor: number | null;
}

/** ICC >= 0,75: a medida e repetivel o bastante para o protocolo. */
export const ICC_MINIMO = 0.75;
/** Abaixo disso de pares, o ICC nao se sustenta e nao e mostrado. */
export const MINIMO_PARES = 5;

type Lado = ObservacaoDeSessao['lado'];
const chave = (o: Pick<ObservacaoDeSessao, 'trait' | 'lado'>) => `${o.trait}|${o.lado}`;

function porMedida(obs: readonly ObservacaoDeSessao[]) {
  const m = new Map<string, ObservacaoDeSessao[]>();
  for (const o of obs) if (o.valor != null) m.set(chave(o), [...(m.get(chave(o)) ?? []), o]);
  return m;
}

export interface LinhaDaDupla {
  trait: string;
  lado: Lado;
  pares: number;
  icc: ResultadoIcc | null;
  blandAltman: BlandAltman | null;
  /** null = poucos pares para dizer. */
  entra: boolean | null;
}

/**
 * Dupla medicao da rotina: em cada ficha, a 1a rodada contra a 2a. O par de
 * avaliadores muda de ficha para ficha, entao o modelo e o ICC(1,1). As piores
 * medidas vem primeiro.
 */
export function repetibilidadeDaDupla(obs: readonly ObservacaoDeSessao[], minimo = MINIMO_PARES): LinhaDaDupla[] {
  return [...porMedida(obs).entries()]
    .map(([k, lista]): LinhaDaDupla => {
      const [trait, lado] = k.split('|') as [string, Lado];
      const fichas = new Map<string, { a?: number; b?: number }>();
      for (const o of lista) {
        const f = fichas.get(o.ficha) ?? {};
        if (o.rodada === 1) f.a = o.valor!;
        if (o.rodada === 2) f.b = o.valor!;
        fichas.set(o.ficha, f);
      }
      const pares = [...fichas.values()].filter((f): f is { a: number; b: number } => f.a != null && f.b != null);
      if (pares.length < minimo) return { trait, lado, pares: pares.length, icc: null, blandAltman: null, entra: null };
      const icc = icc11(pares.map((p) => [p.a, p.b]));
      return {
        trait,
        lado,
        pares: pares.length,
        icc,
        blandAltman: blandAltman(
          pares.map((p) => p.a),
          pares.map((p) => p.b),
        ),
        entra: icc.icc >= ICC_MINIMO,
      };
    })
    .filter((l) => l.pares > 0)
    .sort((x, y) => (x.icc?.icc ?? 2) - (y.icc?.icc ?? 2));
}

export interface LinhaDoEstudo {
  trait: string;
  lado: Lado;
  /** ICC(2,1) entre avaliadores, na 1a rodada, so com as fichas em que todos mediram. */
  entre: ResultadoIcc | null;
  /** Media, entre avaliadores, do ICC(2,1) da 1a contra a 2a rodada do mesmo avaliador. */
  dentro: { icc: number; avaliadores: number } | null;
  entra: boolean | null;
}

/**
 * Estudo da Fase 0: varios avaliadores, duas rodadas nos mesmos animais.
 * "Entre" diz se avaliadores diferentes concordam; "dentro", se o mesmo
 * avaliador se repete. A medida entra no protocolo com os dois >= 0,75.
 */
export function repetibilidadeDoEstudo(obs: readonly ObservacaoDeSessao[]): LinhaDoEstudo[] {
  return [...porMedida(obs).entries()]
    .map(([k, lista]): LinhaDoEstudo => {
      const [trait, lado] = k.split('|') as [string, Lado];
      const avaliadores = [...new Set(lista.map((o) => o.avaliador))];
      const valor = (f: string, a: string, r: number) => lista.find((o) => o.ficha === f && o.avaliador === a && o.rodada === r)?.valor;

      // Entre avaliadores: 1a rodada, desenho balanceado.
      const fichas = [...new Set(lista.map((o) => o.ficha))];
      const completas = fichas.filter((f) => avaliadores.every((a) => valor(f, a, 1) != null));
      const entre =
        avaliadores.length >= 2 && completas.length >= 2
          ? icc21(completas.flatMap((f) => avaliadores.map((a) => ({ sujeito: f, avaliador: a, valor: valor(f, a, 1)! }))))
          : null;

      // Dentro do avaliador: 1a x 2a rodada, avaliador por avaliador.
      const porAvaliador = avaliadores.flatMap((a) => {
        const comAsDuas = fichas.filter((f) => valor(f, a, 1) != null && valor(f, a, 2) != null);
        if (comAsDuas.length < 2) return [];
        return [
          icc21(comAsDuas.flatMap((f) => [1, 2].map((r) => ({ sujeito: f, avaliador: String(r), valor: valor(f, a, r)! })))).icc,
        ];
      });
      const dentro = porAvaliador.length
        ? { icc: Number((porAvaliador.reduce((x, y) => x + y, 0) / porAvaliador.length).toFixed(4)), avaliadores: porAvaliador.length }
        : null;

      return { trait, lado, entre, dentro, entra: entre && dentro ? entre.icc >= ICC_MINIMO && dentro.icc >= ICC_MINIMO : null };
    })
    .sort((x, y) => (x.entre?.icc ?? 2) - (y.entre?.icc ?? 2));
}

export interface LinhaFotoCampo {
  trait: string;
  pares: number;
  /** Diferenca foto - campo, e os limites de concordancia (Bland-Altman). */
  blandAltman: BlandAltman | null;
}

/**
 * Fotogrametria contra goniometro (Fase 1): em cada ficha, o angulo da 1a
 * medicao de campo contra o angulo medido na foto. Correlacao nao serve: os
 * dois metodos podem andar juntos e ficar sempre 3 graus distantes. O que diz
 * se a foto substitui o campo e o vies e a largura dos limites.
 */
export function concordanciaFotoCampo(
  obs: readonly (ObservacaoDeSessao & { metodo: 'campo' | 'foto' | 'sensor' })[],
  minimo = MINIMO_PARES,
): LinhaFotoCampo[] {
  const porTrait = new Map<string, Map<string, { campo?: number; foto?: number }>>();
  for (const o of obs) {
    if (o.valor == null || o.lado !== 'NA') continue;
    if (!(o.metodo === 'foto' || (o.metodo === 'campo' && o.rodada === 1))) continue;
    const fichas = porTrait.get(o.trait) ?? new Map();
    const f = fichas.get(o.ficha) ?? {};
    if (o.metodo === 'foto') f.foto ??= o.valor;
    else f.campo ??= o.valor;
    fichas.set(o.ficha, f);
    porTrait.set(o.trait, fichas);
  }
  return [...porTrait.entries()]
    .map(([trait, fichas]) => {
      const pares = [...fichas.values()].filter((f): f is { campo: number; foto: number } => f.campo != null && f.foto != null);
      return {
        trait,
        pares: pares.length,
        blandAltman:
          pares.length >= minimo
            ? blandAltman(
                pares.map((p) => p.campo),
                pares.map((p) => p.foto),
              )
            : null,
      };
    })
    .filter((l) => l.pares > 0)
    .sort((x, y) => x.trait.localeCompare(y.trait));
}
